import { cloneDeep } from 'lodash';
import { getValueObject } from '@/modules/reporting/utils';
import { getDefaultChartParameters } from '../services/ReportingService';
import {
  callLogDefaultColStateAdmin,
  callLogDefaultColStatePub,
  callLogDefaultColStateBuyer,
  defaultCallLogColDef
} from '../callLogsDefaultColumnState';
import insightDefaultColumnState, { defaultInsightColDefs } from '../insightsColumnState';

export const INSIGHT_REPORT_TYPE = 'insights';
export const CALLLOG_REPORT_TYPE = 'callLogs';

export const histogramAggregations = {
  'year': 'datesByYear',
  'month': 'datesByYearMonth',
  'week': 'datesByYearMonthWeek',
  'day': 'datesByYearMonthDay',
  'hour': 'datesByYearMonthDayHour',
  'minute': 'datesByYearMonthDayHourMinute'
};

function getDefaultInsightGridState(groupBys, columnState) {
  return {
    rowGroupCols: [
      ...groupBys.map(groupBy => {
        return getValueObject({
          aggFunc: undefined,
          field: groupBy.field,
          id: groupBy.field,
          displayName: groupBy.displayName
        });
      })
    ],
    valueCols: null,
    pivotCols: [],
    columnState: columnState?.length ? columnState : cloneDeep(insightDefaultColumnState),
    pivotMode: false,
    filterModel: [],
    expandedGroupIds: []
  };
}

function getDefaultGroupByChartData() {
  return {
    column: {
      defaultData: [],
      drilleddownData: {}, // drilleddownData: { 'CampaignId': { point, data } }
      showLoader: false
    },
    pie: {
      defaultData: [],
      drilleddownData: {},
      showLoader: false
    }
  };
}

function getDefaultChartState() {
  return {
    totals: {},
    selectedParameters: getDefaultChartParameters(),
    column: {
      drilleddownTo: []
    },
    pie: {
      drilleddownTo: []
    }
  };
}

function getDefaultCallLogColStateByRole(role) {
  if (role === 'buyer') {
    return [...callLogDefaultColStateBuyer];  
  }
  if (role === 'publisher') {
    return [...callLogDefaultColStatePub];  
  }
  return [...callLogDefaultColStateAdmin];
}

export const getDefaultCallLogsGridState = function(role) {
  return {
    valueCols: null,
    columnState: getDefaultCallLogColStateByRole(role),
    filterModel: [],
    rowGroupCols: []
  };
};

export const getInitialGroupingSettings = function (groupBys, columnState) {
  return {
    gridState: getDefaultInsightGridState(groupBys, columnState),
    chartStates: getDefaultChartState(),
  };
};

export const getInitialInsight = function() {
  return {
    id: undefined,
    name: '',
    visibleAccountWide: false,
    visibleRoles: [],
    reportType: INSIGHT_REPORT_TYPE, // this could be 'insights'/'callLogs'
    settings: {
      selectedChartType: 'table',
      selectedGroupBy: undefined,
      groupSettings: {},
      filterCriteria: [],
      dateRange: {
        timeZoneId: undefined,
        quickRangeSelector: 'Today',
        timeRange: null,
        isCustomRange: false
      },
      autoReload: false
    }
  };
};

export const getDefaultInsightData = function() {
  return {
    refreshGridData: 0,
    lastUpdated: new Date(),
    chartAllParameters: [],
    chartData: getDefaultGroupByChartData(),
    dataCache: {
      cacheKey: '',
      data: []
    }
  };
};

export const getInitialCallLog = function() {
  return {
    id: undefined,
    name: '',
    visibleAccountWide: false,
    visibleRoles: [],
    reportType: CALLLOG_REPORT_TYPE, // this could be 'insights'/'callLogs'
    settings: {
      viewAs: { icon: 'typcn-user', name: 'Admin', id: 'admin' },
      histogramSettings: {
        groupTimeInterval: 'auto'
      },
      filterCriteria: [],
      gridSettings: getDefaultCallLogsGridState(),
      dateRange: {
        timeZoneId: undefined,
        quickRangeSelector: 'Today',
        timeRange: null,
        isCustomRange: false
      },
      quickInsightsSettings: {
        selectTabGrouping: 'campaignName',
        columnState: [],
      },
      autoReload: false
    }
  };
};

export const getDefaultCallLogData = function() {
  return {
    histogram: [],
    showHistogramLoader: false,
    callLogs: [],
    callLogDetails: {},
    lastUpdated: new Date(),
    refreshGridData: 0,
    dataCache: {
      quickInsights: {},
      histogram: {}
    },
    exportReportStatus: undefined
  };
};

export default {
  savedInsights: [],

  completedCampaignsList: [],

  callLogsData: {
    histogram: [],
    showHistogramLoader: false
  },

  insight: {
    data: {
      unsaved: getDefaultInsightData(),
    },
    reports: {                                // The structure would be { id: { ...commonProperties, savedState: {} } }. Only the `savedState` object would be saved in the backend
      unsaved: getInitialInsight()
    },
    colDefs: [...defaultInsightColDefs],
    colDefsLoadedFromAPI: false,
    activeId: 'unsaved' // For now, we use this insight ID, then we slowly change our actions to 
  },

  callLog: {
    data: {
      unsaved: getDefaultCallLogData()
    },
    reports: {                              // The structure would be { id: { ...commonProperties, savedState: {} } }. Only the `savedState` object would be saved in the backend
      unsaved: getInitialCallLog()
    },
    colDefs: [...defaultCallLogColDef],
    colDefsLoadedFromAPI: false,
    activeId: 'unsaved',
    recentCallTags: [],
    sectionsVisibility: {
      quickInsights: true,
      timeline: true,
      callLog: true
    },
    showExportLoader: false
  },

  fieldAutoCompleteValues: {},

  tags: [], // TODO: move this to a it's own tag state

  tagAutoCompleteValues: {}, // TODO: move this to a it's own tag state

  exportInsightData: {
    shouldExport: false,
    params: {
      allColumns: false,
      sheetName: 'Insights'
    }
  },

  currentView: ''

};
